import type {
  ComponentPropsWithRef,
  ComponentPropsWithoutRef,
  ElementType,
  FunctionComponent,
  ReactElement,
  ReactNode,
  Ref,
  SVGProps,
} from 'react';
import React from 'react';

import classNames from 'classnames';

import styles from './Button.module.css';

export const ButtonKind = [
  'blue',
  'green',
  'grey',
  'orange',
  'red',
  'teal',
  'primary',
  'ghost',
  'outline',
  'destructive',
] as const;
export type ButtonKindType = (typeof ButtonKind)[number];

export const ButtonSize = ['medium', 'small', 'tiny'];
type ButtonSizeType = (typeof ButtonSize)[number];

export type ButtonProps<T extends ElementType> = ComponentPropsWithoutRef<T> & {
  as?: T;
  children?: ReactNode;
  kind?: ButtonKindType;
  size?: ButtonSizeType;
  rounded?: boolean;
  outlined?: boolean;
  block?: boolean;
  light?: boolean;
  inverse?: boolean;
  iconBefore?: FunctionComponent<SVGProps<SVGSVGElement>>;
  iconAfter?: FunctionComponent<SVGProps<SVGSVGElement>>;
};

type ButtonComponent = <T extends ElementType = 'button'>(
  props: ButtonProps<T> & { ref?: Ref<T> },
) => ReactElement | null;

function Button<T extends ElementType = 'button'>(
  {
    as,
    kind = 'grey',
    size = 'medium',
    rounded = false,
    outlined = false,
    light = false,
    inverse = false,
    iconBefore: IconBefore,
    iconAfter: IconAfter,
    block,
    className,
    children,
    ...rest
  }: ButtonProps<T>,
  ref: ComponentPropsWithRef<T>['ref'],
) {
  const Component = as ?? 'button';
  // The caller's className is added to ours, never spread over it: spread
  // after `className={classes}`, it replaced every class here, and an absent
  // one (undefined) left the browser's bare button.
  const classes = classNames(styles.Button, className, {
    [styles[kind]]: kind,
    [styles[size]]: size,
    [styles.rounded]: rounded,
    [styles.outlined]: outlined,
    [styles.block]: block,
    [styles.light]: light,
    [styles.inverse]: inverse,
  });

  return (
    <Component ref={ref} className={classes} {...rest}>
      {IconBefore && <IconBefore className={styles.IconBefore} />}
      <span className={styles.ButtonChild}>{children}</span>
      {IconAfter && <IconAfter className={styles.IconAfter} />}
    </Component>
  );
}

export default React.forwardRef<HTMLButtonElement>(Button) as ButtonComponent;
